import React from "react";
import { Formik, Form, ErrorMessage, useFormikContext } from "formik";
import * as Yup from "yup";

import { Button, CheckboxInput } from "../../../../components";

import styles from "./AcceptTermsForm.module.scss";

import { ValidMessages } from "../../../../resources/locale";
import { useAppDispatch } from "../../../../redux/store";
import { shallowEqual, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { isAuthenticatedSelector } from "../../../../redux/profile/authAndReg/selectors";
import { setShowWindow } from "../../../../redux/profile/authAndReg/slice";
import { cartSelector } from "../../../../redux/cart/selectors";
import { setAcceptedNotices } from "../../../../redux/cart/slice";
import { selectNoticesFor, isNoticeForMembership } from "../../../../redux/notices/selectors";
import { INotice } from "../../../../redux/notices/types";
import { ISelectedSubscr } from "../../../../redux/subscriptions/types";

import { WindowNames } from "../../../../resources/constants";

import { AcceptTermsFormPropsType, AcceptTermsFormValues } from "./types";

const getNoticeFieldName = (noticeId: number) => `notice_${noticeId}`;

// Добавляет в форму поля для объявлений, пришедших уже после монтирования
// (без enableReinitialize, чтобы не сбрасывать уже отмеченные галочки)
const NoticeFieldsSync: React.FC<{ notices: INotice[] }> = ({ notices }) => {
  const { values, setFieldValue } = useFormikContext<AcceptTermsFormValues>();

  React.useEffect(() => {
    notices.forEach((notice) => {
      const fieldName = getNoticeFieldName(notice.id);
      if (values[fieldName] === undefined) {
        setFieldValue(fieldName, false, false);
      }
    });
  }, [notices, values, setFieldValue]);

  return null;
};

export const AcceptTermsForm: React.FC<AcceptTermsFormPropsType> = (props) => {
  const { totalPrice, setIsOpenModalNoAuth } = props;
  const auth = useSelector(isAuthenticatedSelector);
  const { totalCartItems } = useSelector(cartSelector);
  const allCheckoutNotices = useSelector(selectNoticesFor("CHECKOUT_CONSENT"), shallowEqual);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Галочки-объявления нужны, только если в корзине есть подходящий абонемент
  // (пустой membership_ids — любой абонемент; корзина только с арендой — ничего)
  const checkoutNotices = React.useMemo(() => {
    const subscriptionItems = Object.values(
      totalCartItems?.subscriptions || {}
    ) as ISelectedSubscr[];
    const membershipIds = subscriptionItems
      .map((item) => item?.subscription?.id)
      .filter((membershipId) => membershipId !== undefined && membershipId !== null);

    if (membershipIds.length === 0) {
      return [];
    }

    return allCheckoutNotices.filter((notice) =>
      membershipIds.some((membershipId) => isNoticeForMembership(notice, membershipId))
    );
  }, [totalCartItems, allCheckoutNotices]);

  const initialValues = React.useMemo(() => {
    const values: AcceptTermsFormValues = {
      acceptTerms: false,
      acceptRules: false,
      acceptOffer: false,
    };
    checkoutNotices.forEach((notice) => {
      values[getNoticeFieldName(notice.id)] = false;
    });
    return values;
    // Только при монтировании: новые объявления добавит NoticeFieldsSync
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validationSchema = React.useMemo(() => {
    const noticeFields: { [key: string]: Yup.BooleanSchema<boolean | undefined> } = {};
    checkoutNotices.forEach((notice) => {
      noticeFields[getNoticeFieldName(notice.id)] = Yup.bool()
        .required(ValidMessages.inputs.acceptNotice.REQUIRED)
        .oneOf([true], ValidMessages.inputs.acceptNotice.REQUIRED);
    });

    return Yup.object({
      acceptTerms: Yup.bool().oneOf([true], ValidMessages.inputs.acceptTerms.REQUIRED),
      acceptRules: Yup.bool().oneOf([true], ValidMessages.inputs.acceptRules.REQUIRED),
      acceptOffer: Yup.bool().oneOf([true], ValidMessages.inputs.acceptOffer.REQUIRED),
      ...noticeFields,
    });
  }, [checkoutNotices]);

  const handleSubmit = async (values: AcceptTermsFormValues) => {
    if (!auth) {
      setIsOpenModalNoAuth(true);
      dispatch(setShowWindow(WindowNames.AUTH));
      return;
    }

    // Запоминаем, с чем согласился клиент, — уйдёт в данные заказа при оплате
    dispatch(
      setAcceptedNotices(
        checkoutNotices
          .filter((notice) => values[getNoticeFieldName(notice.id)] === true)
          .map((notice) => ({ id: notice.id, text: notice.text }))
      )
    );

    navigate("/payment");
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {(formik) => {
        return (
          <Form className={styles.acceptForm}>
            <NoticeFieldsSync notices={checkoutNotices} />

            <CheckboxInput
              name="acceptTerms"
              label="Ознакомлен, что оплата за абонементы и аренду залов возврату не подлежит"
            />
            <ErrorMessage
              name="acceptTerms"
              component="div"
              className={styles.acceptForm_error}
            />

            {checkoutNotices.map((notice) => (
              <React.Fragment key={notice.id}>
                <CheckboxInput name={getNoticeFieldName(notice.id)} label={notice.text} />
                <ErrorMessage
                  name={getNoticeFieldName(notice.id)}
                  component="div"
                  className={styles.acceptForm_error}
                />
              </React.Fragment>
            ))}

            <CheckboxInput
              name="acceptRules"
              label="Я принимаю правила клуба"
              links={[
                { text: "правила клуба", to: "/rules" }
              ]}
            />
            <ErrorMessage
              name="acceptRules"
              component="div"
              className={styles.acceptForm_error}
            />

            <CheckboxInput
              name="acceptOffer"
              label="Я принимаю условия оферты"
              links={[
                { text: "условия оферты", to: "/offer" }
              ]}
            />
            <ErrorMessage
              name="acceptOffer"
              component="div"
              className={styles.acceptForm_error}
            />

            <Button
              type="submit"
              newStyles={styles.button}
              disabled={formik.isSubmitting}
            >
              <span>Оплатить</span>
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};
